import React, { useState } from "react";
import { Formik, Form } from "formik";
import PropTypes from "prop-types";

// Formik
import * as Yup from "yup";

// Material
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";

// Config
import colors from "../../config/colors";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";

// Components
import Button from "../Button";
import Close from "../Close";
import CustomField from "../CustomField";
import DoneButton from "../DoneButton";
import Text from "../Text";
import UserAddition from "../UserAddition";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3)
  },
  title: {
    marginBottom: theme.spacing(1)
  },
  field: {
    marginBottom: theme.spacing(1)
  },
  addingHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 2
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end"
  },
  addButton: {
    display: "flex",
    alignSelf: "flex-end",
    width: "auto",
    textTransform: "uppercase",
    fontWeight: "normal",
    padding: theme.spacing(1.5),
    color: colors.dodgerBlue,
    margin: theme.spacing(2),
    marginLeft: "auto"
  },
  doneButton: {
    textTransform: "uppercase",
    fontWeight: "normal",
    color: colors.dodgerBlue
  }
}));

const validationSchema = Yup.object().shape({ value: Yup.string() });

const normalize = value => value.trim();
const toApiValue = value => (normalize(value) === "" ? null : value);

// Shared by injury (description/treatment) and physical damage
// (description/estimatedCost) - each domain injects its own onAdd/onUpdate
// and buildUpdateForm (the PUT body shape differs per domain). canAddMore
// only applies to the append-only (!isDraft) history view - a draft's
// inline-edit view has nothing to gate the same way.
const DetailValue = ({
  addButtonText,
  buildUpdateForm,
  canAddMore,
  caseId,
  content,
  isDraft,
  isUpdating,
  isUploadingAdd,
  itemId,
  name,
  onAdd,
  onUpdate,
  title
}) => {
  const entry = content[0] || null;
  const [isEditing, setIsEditing] = useState(!entry || !entry.text);
  const [isAdding, setIsAdding] = useState(false);
  const { trackException } = useAppInsights();
  const classes = useStyles();

  if (!isDraft) {
    const handleAddSubmit = ({ value }, { setSubmitting, resetForm }) => {
      onAdd({
        texts: [value],
        onFinish: success => {
          setSubmitting(false);
          if (success) {
            resetForm();
            setIsAdding(false);
          } else {
            trackException(
              `Failed to add ${name} to ${itemId} at case: ${caseId}`
            );
          }
        }
      });
    };

    return (
      <div className={classes.root}>
        <Text className={classes.title} type="heading4">
          {title}
        </Text>
        {content.map(item => (
          <UserAddition
            key={`${item.author.id}-${item.creationDateTime}`}
            author={item.author}
            date={item.creationDateTime}
            text={item.text}
          />
        ))}
        {canAddMore && (
          <Formik
            initialValues={{ value: "" }}
            onSubmit={handleAddSubmit}
            validationSchema={validationSchema}
          >
            {({ values, submitForm, isSubmitting, errors, resetForm }) =>
              isAdding ? (
                <Form>
                  <div className={classes.addingHeader}>
                    <Text type="valueLabel">
                      {`${title} ${content.length + 1}`}
                    </Text>
                    <IconButton
                      onClick={() => {
                        resetForm();
                        setIsAdding(false);
                      }}
                    >
                      <Close />
                    </IconButton>
                  </div>
                  <CustomField
                    name="value"
                    autoComplete="off"
                    multiline
                    error={errors.value}
                    value={values.value}
                    className={classes.field}
                    disabled={isSubmitting}
                  />
                  <div className={classes.actions}>
                    <DoneButton
                      className={classes.doneButton}
                      disabled={isSubmitting || values.value.trim() === ""}
                      isLoading={isUploadingAdd}
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              ) : (
                <Button
                  className={classes.addButton}
                  color="primary"
                  disableElevation
                  regular
                  onClick={() => setIsAdding(true)}
                >
                  {addButtonText}
                </Button>
              )
            }
          </Formik>
        )}
      </div>
    );
  }

  const handleSubmit = ({ value }, { setSubmitting }) => {
    const isEmpty = normalize(value) === "";

    // Skip the PUT if unchanged
    if (normalize(value) === normalize((entry && entry.text) || "")) {
      setSubmitting(false);
      setIsEditing(isEmpty);
      return;
    }

    onUpdate({
      form: buildUpdateForm(toApiValue(value)),
      onFinish: success => {
        setSubmitting(false);
        if (success) setIsEditing(isEmpty);
        else
          trackException(
            `Failed to update ${name} for ${itemId} at case: ${caseId}`
          );
      }
    });
  };

  return (
    <div className={classes.root}>
      <Text className={classes.title} type="heading4">
        {title}
      </Text>
      <Formik
        initialValues={{ value: (entry && entry.text) || "" }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values, submitForm, isSubmitting, errors, resetForm }) =>
          isEditing ? (
            <Form>
              <CustomField
                name="value"
                autoComplete="off"
                multiline
                error={errors.value}
                value={values.value}
                className={classes.field}
                disabled={isSubmitting}
              />
              <div className={classes.actions}>
                <DoneButton
                  className={classes.doneButton}
                  disabled={isSubmitting}
                  isLoading={isUpdating}
                  onClick={submitForm}
                />
                {entry && entry.text && (
                  <IconButton
                    onClick={() => {
                      resetForm();
                      setIsEditing(false);
                    }}
                  >
                    <Close />
                  </IconButton>
                )}
              </div>
            </Form>
          ) : (
            <UserAddition
              author={entry?.author}
              date={entry?.creationDateTime}
              text={entry?.text}
            />
          )
        }
      </Formik>
    </div>
  );
};

DetailValue.propTypes = {
  addButtonText: PropTypes.string.isRequired,
  buildUpdateForm: PropTypes.func.isRequired,
  canAddMore: PropTypes.bool,
  caseId: PropTypes.string.isRequired,
  content: PropTypes.arrayOf(
    PropTypes.shape({
      creationDateTime: PropTypes.string,
      text: PropTypes.string,
      author: PropTypes.shape({
        id: PropTypes.string,
        fullName: PropTypes.string
      })
    })
  ),
  isDraft: PropTypes.bool.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  isUploadingAdd: PropTypes.bool.isRequired,
  itemId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired
};

DetailValue.defaultProps = {
  canAddMore: true,
  content: []
};

export default DetailValue;
